import React, { useCallback } from 'react';
import ScrollLock from 'react-scrolllock';
import FocusLock from 'react-focus-lock';
import { defineMessages, FormattedMessage } from 'react-intl-next';

import Portal from '@atlaskit/portal';
import Button from '@atlaskit/button/standard-button';
import { layers } from '@atlaskit/theme/constants';

import { useSessionData } from '@confluence/session-data';

import OriginTracing from '@atlassiansox/origin-tracing';

import {
	LockScreenContainer,
	LockScreenBlanket,
	LockScreenDialog,
	LockScreenDialogActions,
} from './styled';
import { ATLASSIAN_START_NON_PROD_URL, ATLASSIAN_START_PROD_URL } from './constants';
import { useAnalytics } from './Analytics';

// Above everything on the screen
const LOCK_SCREEN_Z_INDEX = layers.tooltip() - 1;

const i18n = defineMessages({
	goToStart: {
		id: 'experiment-lock-screen.go-to-start',
		defaultMessage: 'Go to Atlassian Start',
		description: 'Button label for a link that navigates to start.atlassian.com',
	},
});

export const NonDismissibleWrapper = ({
	children,
	isDarkMode,
	originTracing = new OriginTracing({ product: 'confluence' }),
}: {
	children: React.ReactNode;
	isDarkMode: boolean;
	originTracing?: OriginTracing;
}) => {
	const { environment } = useSessionData();
	const { fireAnalytics } = useAnalytics();

	const handleClickGoToStart = useCallback(() => {
		fireAnalytics({
			type: 'sendUIEvent',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'goToStart',
			attributes: {
				...originTracing.toAnalyticsAttributes({ hasGeneratedId: true }),
			},
		});
	}, [fireAnalytics, originTracing]);

	const baseStartUrl =
		environment === 'PRODUCTION' ? ATLASSIAN_START_PROD_URL : ATLASSIAN_START_NON_PROD_URL;

	const startUrl = originTracing.addToUrl(baseStartUrl);

	return (
		<Portal zIndex={LOCK_SCREEN_Z_INDEX}>
			<FocusLock
				// eslint-disable-next-line jsx-a11y/no-autofocus
				autoFocus={false}
			>
				<ScrollLock />
				<LockScreenContainer>
					<LockScreenBlanket isDarkMode={isDarkMode}>
						<LockScreenDialog>
							{children}
							<LockScreenDialogActions>
								<Button
									appearance="subtle-link"
									href={startUrl}
									target="_blank"
									onClick={handleClickGoToStart}
								>
									<FormattedMessage {...i18n.goToStart} />
								</Button>
							</LockScreenDialogActions>
						</LockScreenDialog>
					</LockScreenBlanket>
				</LockScreenContainer>
			</FocusLock>
		</Portal>
	);
};
